import React from 'react';
import { FileText, Shield, Heart, AlertTriangle, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const SocietyPolicy = () => {
  const policies = [
    {
      title: 'Feeding Guidelines',
      icon: Heart,
      color: 'from-green-400 to-emerald-500',
      items: [
        'Designated feeding areas must be used at all times',
        'Feeding times: 7-8 AM and 6-7 PM daily',
        'Clean up the area immediately after feeding',
        'Use proper containers and avoid littering',
        'Report any aggressive behavior immediately',
      ]
    },
    {
      title: 'Safety Protocols',
      icon: Shield,
      color: 'from-blue-400 to-sky-500',
      items: [
        'Maintain safe distance from unfamiliar animals',
        'Never approach animals showing signs of aggression',
        'Keep children supervised around stray animals',
        'Report injured or sick animals to welfare volunteers',
        'Do not attempt to handle aggressive animals',
      ]
    },
    {
      title: 'Resident Responsibilities',
      icon: CheckCircle,
      color: 'from-purple-400 to-violet-500',
      items: [
        'Respect designated feeding areas and schedules',
        'Cooperate with sterilization and vaccination drives',
        'Report incidents promptly through proper channels',
        'Support volunteer efforts in animal welfare',
        'Follow society guidelines for pet ownership',
      ]
    },
    {
      title: 'Prohibited Activities',
      icon: AlertTriangle,
      color: 'from-red-400 to-orange-500',
      items: [
        'Harming or abusing stray animals in any manner',
        'Feeding animals in unauthorized areas',
        'Poisoning or attempting to harm animals',
        'Obstructing welfare volunteers during their duties',
        'Relocating animals without proper authorization',
      ]
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="bg-gradient-to-br from-violet-400 to-purple-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white p-3 rounded-xl shadow-lg">
                <FileText className="w-8 h-8 text-violet-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-white font-heading" data-testid="policy-title">
                Society Animal Policy
              </h1>
            </div>
            <p className="text-lg text-white/90 font-medium">
              Guidelines for harmonious coexistence with stray animals in our society
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg border border-orange-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-text-primary mb-4 font-heading">Our Commitment</h2>
          <p className="text-text-secondary leading-relaxed mb-4">
            Our society is committed to the welfare of stray animals while ensuring the safety and comfort of all residents. 
            We believe in compassionate coexistence and follow the guidelines set by the Animal Welfare Board of India and 
            local municipal authorities.
          </p>
          <p className="text-text-secondary leading-relaxed">
            All policies are designed to create a safe environment for both humans and animals, promoting responsible 
            care and mutual respect.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {policies.map((policy, index) => {
            const Icon = policy.icon;
            return (
              <motion.div
                key={policy.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden hover:shadow-xl transition-all"
                data-testid={`policy-card-${index}`}
              >
                <div className={`bg-gradient-to-br ${policy.color} p-6`}>
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2 rounded-xl">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white font-heading">{policy.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <ul className="space-y-3">
                    {policy.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3" data-testid={`policy-item-${index}-${idx}`}>
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-text-secondary leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border-2 border-orange-200 p-8 mt-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-orange-600 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold text-text-primary mb-3 font-heading">Important Notice</h3>
              <p className="text-text-secondary leading-relaxed mb-3">
                Violation of these policies may result in penalties as per society bylaws. All residents are expected 
                to cooperate with animal welfare volunteers and managing committee representatives.
              </p>
              <p className="text-text-secondary leading-relaxed">
                For any clarifications or concerns regarding these policies, please contact the Managing Committee 
                Representative or Animal Welfare Volunteer listed in the Emergency Response section.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocietyPolicy;